import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Feature } from "ol";
import { Geometry } from "ol/geom";
import { useFragment } from "react-relay";
import graphql from "babel-plugin-relay/macro";
import styled from "styled-components";

import {
  InspectionfieldStateEnum,
  MapListViewFragment$key,
} from "./__generated__/MapListViewFragment.graphql";
import { getCentroid, getWindowHeight, openNavigation } from "../../utils";
import { PlanMultipleContext } from "../../pages/InspectionMap";
import { Portal } from "./MapPortal";
import { ReactComponent as AnglesLeftIcon } from "../../assets/angles-left.svg";
import { ReactComponent as AnglesRightIcon } from "../../assets/angles-right.svg";
import { StateSorts } from "../../pages/InspectionList/StateHelpers";
import { VECTOR_LAYERS } from "./MapView";
import Button from "../Buttons/Button";
import InspectionField, { FieldCardButtons } from "../InspectionField";
import MapContext from "./MapContext";
import PlanModalContainer, { SelectedInspection } from "../PlanModal";
import useSelectInteraction from "../../hooks/useSelectInteraction";

interface MapListViewProps {
  input: MapListViewFragment$key;
  ids?: string[];
  focused?: string;
  focusedCenter?: number[];
  selectedIds: string[];
  onClick?: (id: string) => void;
  onFocus?: (id: string) => void;
  onClickMarker?: (id: string) => void;
  onSelect?: (id: string) => void;
  onDeselect?: (ids: string[]) => void;
}

const MapListView = ({
  input,
  ids,
  focused,
  selectedIds,
  onClick,
  onFocus,
  onClickMarker,
  onSelect,
  onDeselect,
}: MapListViewProps) => {
  const [, startTransition] = useTransition();
  const { setCenter, setZoom } = useContext(MapContext);
  const listContainerRef = useRef<HTMLDivElement>();
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false);
  const { planMultiple, setPlanMultiple } = useContext(PlanMultipleContext);
  const [expanded, setExpanded] = useState<boolean>(true);
  const data = useFragment(
    graphql`
      fragment MapListViewFragment on InspectionfieldNodeConnection {
        totalCount
        edges {
          node {
            id
            year
            externalId
            state
            plannedDate
            periodStart
            periodEnd
            latestInspection {
              lastModified
              approvedDate
              retryDate
              rejectedDate
            }
            field {
              geometry
            }
            ...InspectionFieldCard
          }
        }
      }
    `,
    input
  );
  const selectedInspections: SelectedInspection[] = useMemo(() => {
    return (
      data.edges
        .filter((edge) =>
          selectedIds.some((selectedId) => selectedId === edge?.node?.id)
        )
        .filter((edge) => edge !== undefined)
        .map((edge) => {
          return {
            id: edge?.node?.id,
            externalId: edge?.node?.externalId,
            year: edge?.node?.year,
          } as SelectedInspection;
        }) || []
    );
  }, [data.edges, selectedIds]);
  const disabledButtons: FieldCardButtons[] | undefined = useMemo(() => {
    return planMultiple
      ? [
          FieldCardButtons.OnClick,
          FieldCardButtons.OnClickFocus,
          FieldCardButtons.OnClickNavigate,
          FieldCardButtons.OnClickPlan,
        ]
      : undefined;
  }, [planMultiple]);
  const filterOnState = useCallback(
    (state: InspectionfieldStateEnum) =>
      data?.edges?.filter((edge) => {
        return (
          ids?.includes(edge?.node?.id || "") && edge?.node?.state === state
        );
      }),
    [data?.edges, ids]
  );
  const handleClickFocus = useCallback(
    (id: string, geometry?: string) => {
      onFocus && onFocus(id);
      const center = geometry && getCentroid(geometry)?.reverse();
      center && setCenter && setCenter(center);
      center && setZoom && setZoom(18);
    },
    [onFocus, setZoom, setCenter]
  );
  const handleClickNavigate = (geometry?: any) => {
    openNavigation(geometry);
  };
  const handleClickMarkers = useCallback(
    (features: Feature<Geometry>[]) => {
      startTransition(() => {
        if (features && features.length > 0) {
          setExpanded(true);
          features.forEach(
            (feature) =>
              onClickMarker && onClickMarker(feature.getProperties().id!)
          );
        }
      });
    },
    [onClickMarker]
  );

  const handlePressCalendarIcon = useCallback(
    (id: string) => {
      onDeselect && onDeselect(selectedIds);
      onSelect && onSelect(id!);
      setShowPlanModal(true);
    },
    [onDeselect, onSelect, selectedIds]
  );
  useEffect(() => {
    const scrollActiveTabIntoView = () => {
      const activeCard = listContainerRef.current?.querySelector(
        `[data-focused="true"]`
      );
      if (activeCard) {
        startTransition(() => {
          activeCard.scrollIntoView({
            behavior: "smooth",
          });
        });
      }
    };
    if (expanded) scrollActiveTabIntoView();
  }, [focused, expanded]);
  useSelectInteraction(handleClickMarkers, VECTOR_LAYERS.FIELDS_MARKERS);
  return (
    <Portal>
      <Container expanded={expanded}>
        <div
          style={{
            color: "#FFF",
            fontFamily: "Roboto",
            display: "flex",
            flexDirection: "column",
            margin: "0px 8px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 8,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: 24,
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <AnglesRightIcon fill="#FFF" width={24} height={24} />
            ) : (
              <AnglesLeftIcon fill="#FFF" width={24} height={24} />
            )}
            {expanded ? "Inklappen" : null}
          </div>
          {expanded ? <span>{ids?.length} beheereenheden</span> : null}
          {showPlanModal && (
            <PlanModalContainer
              show={showPlanModal}
              selectedInspections={selectedInspections}
              onDismiss={() => setShowPlanModal(false)}
              onSuccess={() => {
                onDeselect && onDeselect(selectedIds);
              }}
            />
          )}
        </div>
        {expanded ? (
          <>
            <ListContainer ref={listContainerRef}>
              <>
                {filterOnState("ASSIGNED")
                  ?.sort(StateSorts["ASSIGNED"])
                  ?.map(
                    (e) =>
                      e?.node && (
                        <InspectionField
                          key={e.node.id}
                          map
                          disabledButtons={disabledButtons}
                          field={e?.node!}
                          focused={focused === e?.node?.id}
                          selected={selectedIds?.includes(e?.node?.id)}
                          onClick={() => onClick && onClick(e?.node?.id!)}
                          onClickSelect={
                            planMultiple
                              ? () => onSelect && onSelect(e?.node?.id!)
                              : undefined
                          }
                          onClickFocus={() =>
                            handleClickFocus(
                              e?.node?.id!,
                              e?.node?.field?.geometry
                            )
                          }
                          onClickNavigate={() =>
                            handleClickNavigate(e?.node?.field?.geometry)
                          }
                          onClickRetry={undefined}
                          onClickPlan={handlePressCalendarIcon}
                        />
                      )
                  )}
                {filterOnState("PLANNED")
                  ?.sort(StateSorts["PLANNED"])
                  ?.map(
                    (e) =>
                      e?.node && (
                        <InspectionField
                          key={e.node.id}
                          map
                          disabledButtons={disabledButtons}
                          field={e?.node!}
                          selected={false}
                          focused={focused === e?.node?.id}
                          onClick={() => onClick && onClick(e?.node?.id!)}
                          onClickFocus={() =>
                            handleClickFocus(
                              e?.node?.id!,
                              e?.node?.field?.geometry
                            )
                          }
                          onClickNavigate={() =>
                            handleClickNavigate(e?.node?.field?.geometry)
                          }
                          onClickSelect={undefined}
                          onClickRetry={undefined}
                          onClickPlan={undefined}
                        />
                      )
                  )}
                {filterOnState("IN_PROGRESS")
                  ?.sort(StateSorts["IN_PROGRESS"])
                  ?.map(
                    (e) =>
                      e?.node && (
                        <InspectionField
                          key={e.node.id}
                          map
                          disabledButtons={disabledButtons}
                          field={e?.node!}
                          selected={false}
                          focused={focused === e?.node?.id}
                          onClick={() => onClick && onClick(e?.node?.id!)}
                          onClickFocus={() =>
                            handleClickFocus(
                              e?.node?.id!,
                              e?.node?.field?.geometry
                            )
                          }
                          onClickNavigate={() =>
                            handleClickNavigate(e?.node?.field?.geometry)
                          }
                          onClickSelect={undefined}
                          onClickRetry={undefined}
                          onClickPlan={undefined}
                        />
                      )
                  )}
              </>
            </ListContainer>
            <div
              style={{
                margin: "4px 8px 0px 8px",
                display: "flex",
                flexDirection: "row",
                gap: "8px",
              }}
            >
              {planMultiple ? (
                <>
                  <ButtonContainer>
                    <Button
                      size="full"
                      label="Annuleren"
                      transparentBackground
                      color="white"
                      onClick={() => {
                        onDeselect && onDeselect(selectedIds);
                        setPlanMultiple(false);
                      }}
                    />
                  </ButtonContainer>
                  <ButtonContainer>
                    <Button
                      size="full"
                      label={`Inplannen (${selectedIds.length})`}
                      color="green"
                      disabled={selectedIds.length < 1}
                      onClick={() => setShowPlanModal(true)}
                    />
                  </ButtonContainer>
                </>
              ) : (
                <Button
                  size="full"
                  label="Meerdere BE's inplannen"
                  color="green"
                  onClick={() => {
                    setPlanMultiple(true);
                    onDeselect && onDeselect(selectedIds);
                  }}
                />
              )}
            </div>
          </>
        ) : null}
      </Container>
    </Portal>
  );
};

const Container = styled.div<any>`
  margin: 15px;
  background: rgba(33, 37, 41, 0.8);
  border-radius: 5px;
  padding: 8px 0px;
  position: relative;
  height: calc(-170px + ${getWindowHeight()});
  width: fit-content;
  float: right;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ListContainer = styled.div<any>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 48px;
  overflow-y: auto;
  padding: 0px 4px 0px 8px;
  margin-right: 4px;
`;

const ButtonContainer = styled.div<any>`
  flex-grow: 1;
`;

export default MapListView;
